export type DurationUnit =
  | "s"
  | "min"
  | "h"
  | "day"
  | "week"
  | "month"
  | "year";
export type LengthUnit = "in" | "mm" | "cm" | "m";
export type AreaUnit = "in2" | "mm2" | "cm2" | "m2";
type VolumeUnit = "in3" | "mm3" | "cm3" | "m3";
type MassUnit = "g" | "kg" | "lb";
type MoneyUnit = "USD";
type PowerUnit = "kW";
type PressureUnit = "kPSI";

export type AnyUnit =
  | DurationUnit
  | LengthUnit
  | AreaUnit
  | VolumeUnit
  | MoneyUnit
  | MassUnit
  | PowerUnit
  | PressureUnit;

export interface Quantity<Unit extends AnyUnit> {
  value: number;
  unit: Unit;
}

export interface Rate<
  NumeratorUnit extends AnyUnit,
  DenominatorUnit extends AnyUnit,
> {
  value: number;
  nunit: NumeratorUnit;
  dunit: DenominatorUnit;
}

const UNIT_CONVERSIONS: Record<string, Record<string, number>> = {
  Duration: {
    s: 1,
    min: 60,
    h: 3600,
    day: 86400,
    week: 604800,
    month: 2628000,
    year: 31536000,
  },
  Length: { mm: 0.001, cm: 0.01, m: 1, in: 0.0254 },
  Area: { mm2: 0.000001, cm2: 0.0001, m2: 1, in2: 0.00064516 },
  Volume: { mm3: 0.000000001, cm3: 0.000001, m3: 1, in3: 1.63871e-5 },
  Mass: { g: 0.001, kg: 1, lb: 0.453592 },
  Money: { USD: 1 },
  Power: { kW: 1 },
  Pressure: { kPSI: 1 },
};

export function inferUnitCategory(unit: AnyUnit): string {
  const category = Object.entries(UNIT_CONVERSIONS).find(
    ([_, units]) => unit in units,
  )?.[0];

  if (!category) {
    throw new Error(`Unit "${unit}" not found in any category.`);
  }

  return category;
}

export function convertUnit(
  value: number,
  fromUnit: AnyUnit,
  toUnit: AnyUnit,
): number {
  const category = inferUnitCategory(fromUnit);
  if (!category) {
    throw new Error(`Unknown unit category: ${fromUnit}`);
  }

  if (category !== inferUnitCategory(toUnit)) {
    throw new Error(
      `Cannot convert between units of different categories: ${category} and ${inferUnitCategory(toUnit)}`,
    );
  }

  return (
    (value * UNIT_CONVERSIONS[category][fromUnit]) /
    UNIT_CONVERSIONS[category][toUnit]
  );
}
